import InputValidationComponent from "@/_components/InputValidationComponent"
import { loc } from "@/_services/localization"
import { getUiState, setUiState } from "@/_services/uiState"
import { applyClasses, customEvents, domAttributes, handleAccessibleOnKeyDown, localStorageKeys } from "@/_services/utils"
import React from "react"
import { Col, Panel, Row } from "react-bootstrap"
import { withRouter } from "react-router-dom"

function getInitialState(props) {
  const {
    match: { path },
    title = "",
    collapse,
    cache = true,
  } = props

  let id
  if (cache) {
    id = typeof title === "string" ? (title || "").toLowerCase().replace(/ /g, "-").replace(/\(/g, "").replace(/\)/g, "") : Date.now().toString()

    const expandedFlag = getUiState(`${localStorageKeys.UI_STATE.PANELS_PREFIX}${path}.inner.${id}.isExpanded`)
    if (expandedFlag !== undefined) {
      return {
        id,
        expanded: expandedFlag,
        toggleExpanded: true,
      }
    }
  }

  return {
    id,
    expanded: collapse !== undefined ? !collapse : true,
    toggleExpanded: true,
  }
}

class PanelInner extends React.Component {
  constructor(props) {
    super(props)
    this.state = getInitialState(props)
    this.innerPanelRef = React.createRef()
  }

  componentDidMount() {
    window.addEventListener(customEvents.innerPanel.toggle, this.eventToggle, true)
    this.innerPanelRef.current.addEventListener(customEvents.innerPanel.open, this.openInnerPanel, true)
  }

  componentWillUnmount() {
    window.removeEventListener(customEvents.innerPanel.toggle, this.eventToggle, true)
    this.innerPanelRef.current.removeEventListener(customEvents.innerPanel.open, this.openInnerPanel, true)
  }

  eventToggle = () => {
    const { toggleExpanded } = this.state
    const { collapse } = this.props
    const isCollapsible = collapse !== undefined
    if (isCollapsible) {
      this.setState({ expanded: !toggleExpanded, toggleExpanded: !toggleExpanded })
    }
  }

  openInnerPanel = () => this.toggle(true)

  toggle = forceExpand => {
    const { id } = this.state
    const expanded = forceExpand || !this.state.expanded
    const {
      match: { path },
      title,
      cache = true,
      onSetExpanded,
    } = this.props

    if (cache && title) setUiState(`${localStorageKeys.UI_STATE.PANELS_PREFIX}${path}.inner.${id}.isExpanded`, expanded)
    onSetExpanded && onSetExpanded(expanded)
    this.setState({ expanded })
  }

  render() {
    const { expanded } = this.state
    const {
      title,
      titleIcon,
      titleLinkTo,
      titleSideText,
      category,
      hidePostTitleWhen,
      buttons,
      children,
      collapse,
      titleClassName,
      sectionProps = {},
      className = "",
    } = this.props
    const isCollapsible = collapse !== undefined
    let hideTitleSideText = false
    if (hidePostTitleWhen === "expanded") hideTitleSideText = expanded
    if (hidePostTitleWhen === "collapsed") hideTitleSideText = !expanded

    const titleClasses = applyClasses({
      "panel-title": true,
      "c-pointer": isCollapsible,
      [titleClassName || ""]: true,
    })

    let titleColSize = 12
    if (isCollapsible) titleColSize = 11
    if (expanded && buttons) titleColSize = 6

    const titleContent =
      title || (!hideTitleSideText && titleSideText) ? (
        <>
          {title && (
            <span
              tabIndex={isCollapsible ? "0" : "-1"}
              className={`${titleIcon && typeof title === "string" ? "inline-flex align-items-center" : ""}${isCollapsible ? " outline-accessible" : ""}`}
              onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: () => isCollapsible && this.toggle() })}
            >
              {titleIcon && <i className={`mr-5px ${titleIcon}`} />}
              {typeof title === "string" ? `${loc(title)}` : title}
            </span>
          )}
          {!hideTitleSideText && titleSideText && <span className="white-space-nowrap ml-5px">{titleSideText}</span>}
        </>
      ) : null

    // Same markup as the category of Card.jsx, so that it gets the same style.
    // It is in the title column, right under the title, so that the padding of the column wraps both like the header of a card.
    let categoryContent = null
    if (category) {
      // A category can be a React element, which cannot be in a paragraph when it has blocks
      categoryContent = typeof category === "string" ? <p className="category">{loc(category)}</p> : <div className="category">{category}</div>
    }

    const titleCol =
      titleContent || categoryContent || isCollapsible ? (
        <Col xs={titleColSize} className={titleClasses}>
          {titleContent}

          {titleLinkTo && (
            <i
              className="icn-external-link icn-xs text-link ml-5px"
              onClick={event => {
                event.stopPropagation()
                window.open(window.location.origin + titleLinkTo, "_blank")
              }}
            />
          )}

          {categoryContent}
        </Col>
      ) : null

    const buttonsCol =
      (expanded && buttons) || isCollapsible ? (
        <Col xs={12 - titleColSize} className="flex-center justify-content-end">
          {isCollapsible && !(expanded && buttons) && (
            <i className="section-collapse-icon icn-chevron-right icn-xs pull-right" {...{ [domAttributes.isExpanded]: expanded }} />
          )}
          {expanded && <div onClick={e => e.stopPropagation()}>{buttons}</div>}
        </Col>
      ) : null

    const showTopCol = titleContent || titleCol || buttonsCol || sectionProps?.errorFormat

    return (
      // we need to use a real html tag to be able to set a ref
      <div className={"row panel-inner " + className} ref={this.innerPanelRef}>
        {showTopCol && (
          <Col xs={12}>
            <Row
              onClick={() => {
                if (isCollapsible) this.toggle()
              }}
            >
              {titleCol} {buttonsCol}
              {sectionProps?.errorFormat && <InputValidationComponent message={sectionProps.errorFormat} isSectionError />}
            </Row>
          </Col>
        )}
        <Col xs={12}>
          {/* onToggle is added as a hack to shut the console error from react */}
          <Panel expanded={isCollapsible ? expanded : true} onToggle={() => {}}>
            <Panel.Collapse>
              <Panel.Body>{children}</Panel.Body>
            </Panel.Collapse>
          </Panel>
        </Col>
      </div>
    )
  }
}

export default withRouter(PanelInner)
